<template>
    <tbody>
        <tr>
            <td class="text-primary" @click="expanded = !expanded">
                {{ tariff.name }}
                <v-icon v-if="!expanded" class="text-primary" size="x-small">mdi-arrow-right-bold</v-icon>
                <v-icon v-else class="text-primary" size="x-small">mdi-arrow-up-bold</v-icon>
            </td>
            <td
                v-for="tariffDay in tariff.dailyRates"
                :key="'tariff-day-' + serviceId + '-' + tariff.id + tariffDay.date"
                :class="rateStatusColor(tariffDay)"
            >
                <!-- ZSH: Disabled menu - cell is read-only -->
                <div class="fill" />
            </td>
        </tr>
        <tr v-show="expanded">
            <td>{{ $t('price') }} {{ currency }}</td>
            <td
                v-for="tariffDay in tariff.dailyRates"
                :key="'tariff-day-price-' + serviceId + '-' + tariff.id + tariffDay.date"
                :class="{'bg-red-lighten-4': isClosed(tariffDay)}"
            >
                <v-text-field
                    class="text-center"
                    :model-value="tariffDay.rate.amount"
                    flat
                    hide-details
                    type="number"
                    min="0"
                    :disabled="isPriceDisabled || isClosed(tariffDay)"
                    :readonly="isPriceDisabled"
                    color="black"
                    @change="updateRatePrice(tariffDay.date, $event)"
                />
            </td>
        </tr>
        <tr v-show="expanded">
            <td>{{ $t('supplier.min_length_of_stay') }}</td>
            <td
                v-for="tariffDay in tariff.dailyRates"
                :key="'tariff-min-nights-' + serviceId + '-' + tariff.id + tariffDay.date"
                :class="{'bg-red-lighten-4': isClosed(tariffDay)}"
            >
                <v-text-field
                    class="text-center"
                    :model-value="tariffDay.rate.minNights"
                    flat
                    hide-details
                    type="number"
                    min="0"
                    :disabled="isPriceDisabled || isClosed(tariffDay)"
                    :readonly="isPriceDisabled"
                    color="black"
                    @change="updateRateMinNights(tariffDay.date, $event)"
                />
            </td>
        </tr>
        <tr v-show="expanded">
            <td>{{ $t('supplier.max_length_of_stay') }}</td>
            <td
                v-for="tariffDay in tariff.dailyRates"
                :key="'tariff-max-nights-' + serviceId + '-' + tariff.id + tariffDay.date"
                :class="{'bg-red-lighten-4': isClosed(tariffDay)}"
            >
                <v-text-field
                    class="text-center"
                    :model-value="tariffDay.rate.maxNights"
                    flat
                    hide-details
                    type="number"
                    min="0"
                    :disabled="isPriceDisabled || isClosed(tariffDay)"
                    :readonly="isPriceDisabled"
                    color="black"
                    @change="updateRateMaxNights(tariffDay.date, $event)"
                />
            </td>
        </tr>
    </tbody>
</template>

<script lang="ts">
    import {Component, Prop, Vue} from 'vue-facing-decorator'
    import {supplierStore} from '~src/composables/store-accessor'
    import {isDailyRateClosed} from '~src/utils/daily-rate-status'

    @Component({})
    export class TariffRows extends Vue {
        @Prop() tariff
        @Prop() serviceId
        @Prop() sustainabilityLevel

        expanded = false

        get isPriceDisabled() {
            // ZSH: Price cells editable only for BASIC rooms
            // SUSTAINABLE and ZERO prices are derived from BASIC, so they should not be editable
            const isDisabled = this.sustainabilityLevel === 'SUSTAINABLE' || this.sustainabilityLevel === 'ZERO'
            console.warn('isPriceDisabled:', {
                sustainabilityLevel: this.sustainabilityLevel,
                isDisabled,
                serviceId: this.serviceId,
            })
            return isDisabled
        }

        rateStatus(tariffDay) {
            switch (this.rateStatusColor(tariffDay)) {
                case 'bg-green-lighten-2':
                    return 'bookable'
                case 'bg-red-lighten-2':
                    return 'closed'
                default:
                    return 'no_room'
            }
        }

        isClosed(tariffDay) {
            return isDailyRateClosed(tariffDay.rate)
        }

        rateStatusColor(tariffDay) {
            if (this.isClosed(tariffDay)) {
                return 'bg-red-lighten-2'
            }

            if (tariffDay.rate.active && tariffDay.rate.amount) {
                return 'bg-green-lighten-2'
            }

            return 'bg-grey-lighten-2'
        }

        get currency() {
            return supplierStore.contract.transactionsCurrency
        }

        changeRateStatus(date, value) {
            this.$emit('update', {
                dateFrom: date,
                dateTo: date,
                tariffId: this.tariff.id,
                serviceId: this.serviceId,
                operation: value === 'bookable' ? 'CLEAR_BLOCK' : 'BLOCK',
                dayOfWeeks: [true, true, true, true, true, true, true],
            })
        }

        updateRatePrice(date, value) {
            // Extract the actual numeric value to avoid Vue reactivity metadata
            const numericValue = this.extractNumericValue(value)

            this.$emit('update', {
                dateFrom: date,
                dateTo: date,
                tariffId: this.tariff.id,
                serviceId: this.serviceId,
                dayOfWeeks: [true, true, true, true, true, true, true],
                amount: numericValue,
            })
        }

        updateRateMinNights(date, value) {
            // Extract the actual numeric value to avoid Vue reactivity metadata
            const numericValue = this.extractNumericValue(value)

            this.$emit('update', {
                dateFrom: date,
                dateTo: date,
                tariffId: this.tariff.id,
                serviceId: this.serviceId,
                dayOfWeeks: [true, true, true, true, true, true, true],
                minNights: numericValue,
            })
        }

        updateRateMaxNights(date, value) {
            // Extract the actual numeric value to avoid Vue reactivity metadata
            const numericValue = this.extractNumericValue(value)

            this.$emit('update', {
                dateFrom: date,
                dateTo: date,
                tariffId: this.tariff.id,
                serviceId: this.serviceId,
                dayOfWeeks: [true, true, true, true, true, true, true],
                maxNights: numericValue,
            })
        }

        /**
         * Extract the actual numeric value from input event or reactive object
         * This prevents Vue reactivity metadata from being sent to the API
         */
        extractNumericValue(value) {
            // If it's an event object, get the target value
            if (value && typeof value === 'object' && value.target) {
                return parseFloat(value.target.value) || null
            }

            // If it's a reactive object with Vue metadata, extract the actual value
            if (value && typeof value === 'object' && ('_vts' in value || 'isTrusted' in value)) {
                // Try to find the actual numeric value in the object
                for (const key in value) {
                    if (typeof value[key] === 'number' && !key.startsWith('_') && key !== 'isTrusted') {
                        return value[key]
                    }
                }
                // If no numeric property found, return null
                return null
            }

            // If it's already a plain number or string, parse it
            if (typeof value === 'number') {
                return value
            }

            if (typeof value === 'string') {
                const parsed = parseFloat(value)
                return isNaN(parsed) ? null : parsed
            }

            return null
        }
    }

    export default TariffRows
</script>

<style scoped lang="scss">
    .fill {
        height: 100%;
        pointer-events: none !important;
    }
    .full-width {
        width: 100%;
    }
</style>
